import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import MultiSelect from "../components/MultiSelect";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search as SearchIcon, MapPin, Briefcase, Clock, X, LayoutGrid, List, Download } from "lucide-react";
import { toTagList } from "../lib/format";
import { toast, Toaster } from "sonner";

const VIEW_LS_KEY = "gw_search_view_mode";

const splitCsv = (v) => (v ? v.split(",").map((s) => s.trim()).filter(Boolean) : []);

const filtersFromParams = (sp) => ({
  q: sp.get("q") || "",
  industry: sp.get("industry") || "",
  domain: sp.get("domain") || "",
  current_city: splitCsv(sp.get("current_city")),
  experience: sp.get("experience") || "",
  notice_period: sp.get("notice_period") || "",
  status: sp.get("status") || "",
});

export default function Search() {
  const { user } = useAuth();
  const isSC = user?.role === "super_custodian";
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [dropdowns, setDropdowns] = useState({ industry: [], domain: [], status: [], work_setup: [] });
  const [cities, setCities] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_LS_KEY) || "card");

  useEffect(() => { localStorage.setItem(VIEW_LS_KEY, view); }, [view]);

  useEffect(() => {
    api.get("/meta/dropdowns")
      .then(({ data }) => setDropdowns({
        industry: (data.industry || []).map((o) => o.value),
        domain: (data.domain || []).map((o) => o.value),
        status: (data.status || []).map((o) => o.value),
        work_setup: (data.work_setup || []).map((o) => o.value),
      }))
      .catch(() => {});
    api.get("/meta/cities").then(({ data }) => setCities(data.cities || [])).catch(() => {});
  }, []);

  const buildParams = useCallback((f) => {
    const p = {};
    if (f.q) p.q = f.q;
    if (f.industry) p.industry = f.industry;
    if (f.domain) p.domain = f.domain;
    if (f.current_city?.length) p.current_city = f.current_city.join(",");
    if (f.experience) p.experience = f.experience;
    if (f.notice_period) p.notice_period = f.notice_period;
    if (f.status) p.status = f.status;
    return p;
  }, []);

  const run = useCallback(async (f) => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidates", { params: buildParams(f) });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Sync filters ↔ URL query params, re-run on any URL change (deep-link support).
  useEffect(() => {
    const f = filtersFromParams(searchParams);
    setFilters(f);
    run(f);
  }, [searchParams, run]);

  const onChange = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    // Push current filters into URL — the useEffect above then triggers run().
    const params = buildParams(filters);
    setSearchParams(params, { replace: true });
  };

  const reset = () => {
    setSearchParams({}, { replace: true });
  };

  const downloadFilteredCsv = async () => {
    try {
      const res = await api.get("/export/csv-filtered", { params: buildParams(filters), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `getwork_candidates_filtered_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Downloaded ${total} candidate(s)`);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Search candidates</h1>
          <p className="text-slate-500 mt-1">Search across {total.toLocaleString()} candidates by name, skills, domain, or industry.</p>
        </div>
        <div className="flex items-center gap-2">
          {isSC && (
            <Button data-testid="download-filtered-csv" variant="outline" onClick={downloadFilteredCsv} disabled={total === 0}>
              <Download className="w-4 h-4 mr-1.5" />Download ({total.toLocaleString()})
            </Button>
          )}
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1" data-testid="view-toggle">
            <button
              data-testid="view-card"
              onClick={() => setView("card")}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${view === "card" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            ><LayoutGrid className="w-3.5 h-3.5" />Card</button>
            <button
              data-testid="view-list"
              onClick={() => setView("list")}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            ><List className="w-3.5 h-3.5" />List</button>
          </div>
        </div>
      </div>

      <Card className="p-5 border-slate-200 mb-6">
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-input"
              placeholder="Search by name, skills, domain, industry…"
              className="pl-10 h-11 text-base"
              value={filters.q}
              onChange={(e) => onChange("q", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-medium">Industry</label>
              <Select value={filters.industry || "any"} onValueChange={(v) => onChange("industry", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-industry" className="mt-1"><SelectValue placeholder="Industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any industry</SelectItem>
                  {dropdowns.industry.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Domain / Function</label>
              <Select value={filters.domain || "any"} onValueChange={(v) => onChange("domain", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-domain" className="mt-1"><SelectValue placeholder="Domain / Function" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any domain</SelectItem>
                  {dropdowns.domain.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Current city (multi-select)</label>
              <MultiSelect
                testId="filter-city"
                value={filters.current_city}
                onChange={(v) => onChange("current_city", v)}
                options={cities}
                placeholder="Current city…"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="filter-experience" placeholder="Exp e.g. 5 or 5-8" value={filters.experience} onChange={(e) => onChange("experience", e.target.value)} />
              <Input data-testid="filter-notice" placeholder="Notice ≤ days" value={filters.notice_period} onChange={(e) => onChange("notice_period", e.target.value)} />
              <Select value={filters.status || "any"} onValueChange={(v) => onChange("status", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-status" className="col-span-2"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any status</SelectItem>
                  {(dropdowns.status.length ? dropdowns.status : ["Looking", "Not Looking", "Passive", "Placed", "Follow Up Later"]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button data-testid="reset-filters" type="button" variant="outline" onClick={reset}>
              <X className="w-4 h-4 mr-1.5" />Reset
            </Button>
            <Button data-testid="apply-filters" type="submit" className="bg-indigo-600 hover:bg-indigo-700">
              Apply filters
            </Button>
          </div>
        </form>
      </Card>

      {loading && <div className="text-slate-500">Searching…</div>}

      {!loading && items.length === 0 && (
        <Card className="p-12 text-center border-slate-200">
          <p className="text-slate-500">No candidates match your filters.</p>
        </Card>
      )}

      {!loading && items.length > 0 && view === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((c) => (
            <Link key={c.id} to={`/candidate/${c.id}`} data-testid={`candidate-card-${c.id}`} className="block">
              <Card className="p-5 border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all h-full">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-slate-900 truncate" data-testid="candidate-name">{c.candidate_name}</h3>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                      <Briefcase className="w-3 h-3" />{c.domain || "—"}
                    </p>
                  </div>
                  <StatusBadge status={c.candidate_status} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
                  <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{c.current_city || "—"}</div>
                  <div className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-slate-400" />{c.work_experience ?? "—"} yrs</div>
                  <div className="flex items-center gap-1 col-span-2"><Clock className="w-3 h-3 text-slate-400" />Notice: {c.notice_period ?? "—"} days</div>
                </div>

                {toTagList(c.industry).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{typeof c.industry === "string" ? c.industry : toTagList(c.industry)[0]}</span>
                  </div>
                )}

                {c.nudges?.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-slate-100">
                    {c.nudges.map((n, i) => (
                      <p key={i} className="text-xs italic text-indigo-600 leading-snug" data-testid="candidate-nudge">{n}</p>
                    ))}
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}

      {!loading && items.length > 0 && view === "list" && (
        <Card className="p-0 border-slate-200 overflow-hidden" data-testid="results-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Industry</th>
                  <th className="text-left py-3 px-4 font-semibold">Domain</th>
                  <th className="text-left py-3 px-4 font-semibold">City</th>
                  <th className="text-right py-3 px-4 font-semibold">Exp (yrs)</th>
                  <th className="text-right py-3 px-4 font-semibold">Notice</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`candidate-row-${c.id}`}>
                    <td className="py-2 px-4">
                      <Link to={`/candidate/${c.id}`} className="font-medium text-slate-900 hover:text-indigo-700">{c.candidate_name}</Link>
                      <div className="text-xs text-slate-400">{c.email}</div>
                    </td>
                    <td className="py-2 px-4 text-xs text-slate-700">{typeof c.industry === "string" ? (c.industry || "—") : (toTagList(c.industry)[0] || "—")}</td>
                    <td className="py-2 px-4 text-xs text-slate-700">{typeof c.domain === "string" ? (c.domain || "—") : (toTagList(c.domain)[0] || "—")}</td>
                    <td className="py-2 px-4 text-xs text-slate-700">{c.current_city || "—"}</td>
                    <td className="py-2 px-4 text-right text-xs text-slate-700">{c.work_experience ?? "—"}</td>
                    <td className="py-2 px-4 text-right text-xs text-slate-700">{c.notice_period ?? "—"} d</td>
                    <td className="py-2 px-4"><StatusBadge status={c.candidate_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Layout>
  );
}
